/**
 * Socket Utility Functions
 * 
 * Utilities for managing socket selection and creation for integrations
 */

import { apiService } from '../services/api';
import { IntegrationDefinition } from '../integrations/types';
import { SpaceSocket } from '../services/api';

const SOCKET_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when socket_id is a UUID the extension generate API will accept. */
export function isSocketUuid(socketId: string | null | undefined): boolean {
  return typeof socketId === 'string' && SOCKET_UUID_RE.test(socketId.trim());
}

/** Trailing " API Socket" / " Socket" style labels on default socket names (matches Extension Builder). */
const SOCKET_TRAILING_SOCKET_LABEL = /\s*(API\s*)?Socket\s*$/i;

/**
 * Default Chrome extension display name from a socket when integration name is empty.
 * Strips a trailing API/socket label, then appends " Extension".
 */
export function defaultExtensionDisplayNameFromSocket(
  socket: SpaceSocket | null
): string {
  if (!socket?.socket_name?.trim()) {
    return '';
  }
  const raw = socket.socket_name.trim();
  const base = raw.replace(SOCKET_TRAILING_SOCKET_LABEL, '').trim();
  const label = base || raw;
  return `${label} Extension`;
}

/**
 * Get or create a default socket for an integration
 * 
 * This function:
 * 1. Gets all sockets for the space
 * 2. Finds a default socket matching integration's socket types (prefers socket with "default" in name)
 * 3. If found, returns it
 * 4. If not found, creates a default socket using the first compatible socket type
 * 5. Returns the created socket
 * 
 * @param spaceId - The space ID to get/create socket for
 * @param integration - The integration definition
 * @returns Promise resolving to a SpaceSocket
 */
export async function getOrCreateDefaultSocket(
  spaceId: string,
  integration: IntegrationDefinition
): Promise<SpaceSocket> {
  try {
    // Get all sockets for the space
    const socketList = await apiService.getSocketsForSpace(spaceId);
    
    // Find default socket matching integration's socket types
    // First, try to find a socket with "default" in name
    let defaultSocket = socketList.sockets.find((s) => {
      const socketType = s.socket_config?.template_id;
      const isDefault = s.socket_name.toLowerCase().includes('default');
      const isCompatible = integration.socketTypes.includes(socketType as any);
      return isCompatible && isDefault;
    });
    
    // Fallback: find any compatible socket
    if (!defaultSocket) {
      defaultSocket = socketList.sockets.find((s) => {
        const socketType = s.socket_config?.template_id;
        return integration.socketTypes.includes(socketType as any);
      });
    }
    
    // If we found a socket, return it
    if (defaultSocket) {
      return defaultSocket;
    }
    
    // No compatible socket found - create a default socket using first compatible type
    const socketType = integration.socketTypes[0];
    const socketName = `Default ${socketType.toUpperCase()} Socket`;
    const socketDescription = `Default socket for ${integration.name} integration using ${socketType.toUpperCase()} protocol`;
    
    const newSocket = await apiService.createSocketFromTemplate(
      spaceId,
      socketName,
      socketDescription,
      socketType
    );
    
    return newSocket;
  } catch (error) {
    console.error('Failed to get or create default socket:', error);
    throw error;
  }
}

